import { FC, useCallback } from "react";
import { ShopifyProduct, Store } from "../../../../../common/models";
import { useConnectShopifyProductToStoreMutation, queryShopifyProductsForAdmin } from "../../../datastore/ShopifyProductDataStore";
import { DefaultShopifyProductTableConfig } from "../tables/config/DefaultShopifyProductTableConfig";
import { ShopifyProductForAdmin } from "../../../../../common/tables/shopify/ShopifyProductForAdmin";
import { AbstractTableViewProvider } from "../../../common/tables/view/AbstractTableViewProvider";
import { TableCellFactory } from "../../../common/tables/factory";
import { ShopifyProductTableCellFactory } from "../tables/factory/ShopifyProductTableCellFactory";

const shopifyProductTableCellFactory: TableCellFactory<ShopifyProductForAdmin, ShopifyProduct> = new ShopifyProductTableCellFactory();

export const AdminProductListProvider: FC = ({ children }) => {
    const [
        connectShopifyProductToStore,
        {
            loading: connectMutationLoading,
            error: connectMutationError,
            called: connectMutationCalled,
        },
    ] = useConnectShopifyProductToStoreMutation();

    const onUpdateData = useCallback((
        prevData: ShopifyProduct[],
        id: string | number,
        columnName: keyof ShopifyProductForAdmin,
        value: string | number | boolean | null,
        label?: string,
    ): ShopifyProduct[] => {
        return prevData.map((original: ShopifyProduct) => {
            // Skip over all ShopifyProducts that aren't being updated.
            if (id !== original.id) {
                return original;
            }

            // Handle store assignment – calls connectShopifyProductToStore mutation
            // which auto-creates a ProductListing if none exists.
            if (columnName === "storeName") {
                connectShopifyProductToStore({
                    shopifyProductId: original.id,
                    storeId: value as string,
                });
                return {
                    ...original,
                    store: {
                        ...(((original as any).store) ?? {} as Store),
                        id: value as string,
                        name: label ?? "",
                    },
                };
            }

            return original;
        });
    }, [connectShopifyProductToStore]);

    return (
        <AbstractTableViewProvider
            queryTableData={queryShopifyProductsForAdmin}
            onUpdateRow={onUpdateData}
            updateRowLoading={connectMutationLoading}
            updateRowCalled={connectMutationCalled}
            updateRowError={connectMutationError}
            tableCellFactory={shopifyProductTableCellFactory}
            savedTableConfigProps={{
                tableName: "shopifyProductsForAdmin",
                identifiers: ["v3"],
                defaultConfigValue: DefaultShopifyProductTableConfig,
            }}
        >
            {children}
        </AbstractTableViewProvider>
    );
}
